import React, { useState } from "react";
import { Alert, Badge, Button, Card, Select, TextField } from "stories/Admin";
import useFetch from "hooks/useFetch";
import styles from "./ExternalPage.module.scss";
import {
  BonnetidCatalogSource,
  BonnetidRawLookup,
  isValidSourceId,
} from "./bonnetidTypes";

/**
 * Rådata for én moské rett fra Bonnetid (gammelt eller nytt API), for å se hva som kommer ut derfra.
 * Ingenting lagres i iBønn.
 */
const BonnetidRawPanel: React.FC = () => {
  const { execute, lastError } = useFetch<BonnetidRawLookup>();
  const [source, setSource] = useState<BonnetidCatalogSource>("NEW");
  const [orgNr, setOrgNr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BonnetidRawLookup | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const id = orgNr.trim();

  const lookup = async () => {
    if (!isValidSourceId(id)) return;
    setBusy(true);
    setError("");
    setResult(null);
    setCopied(false);
    const query = new URLSearchParams({ source, orgNr: id });
    const res = (await execute(
      `/api/v1/admin/external-jamat/raw?${query}`,
      "GET",
    )) as BonnetidRawLookup | null;
    setBusy(false);
    if (!res) {
      setError(lastError()?.message || "Kunne ikke hente rådata.");
      return;
    }
    setResult(res);
  };

  const text = result
    ? result.body != null
      ? JSON.stringify(result.body, null, 2)
      : (result.bodyText ?? "")
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const empty =
    result &&
    !result.error &&
    (result.body == null ||
      (Array.isArray(result.body) && result.body.length === 0));

  return (
    <Card
      title="Se rådata fra Bonnetid"
      subtitle="Hent én moské rett fra gammelt eller nytt Bønnetid-API og se svaret. Ingenting lagres i iBønn."
    >
      <form
        className={styles.rawForm}
        onSubmit={(e) => {
          e.preventDefault();
          void lookup();
        }}
      >
        <Select
          label="API"
          value={source}
          disabled={busy}
          onChange={(e) => setSource(e.target.value as BonnetidCatalogSource)}
        >
          <option value="NEW">Nytt Bønnetid-API (mosque_t)</option>
          <option value="LEGACY">Gammelt Bønnetid-API (/mosques/…)</option>
        </Select>
        <TextField
          label="Org.nr. hos Bonnetid"
          placeholder="F.eks. 933279723"
          value={orgNr}
          disabled={busy}
          onChange={(e) => setOrgNr(e.target.value)}
        />
        <Button
          type="submit"
          loading={busy}
          disabled={busy || !isValidSourceId(id)}
        >
          Hent rådata
        </Button>
      </form>

      {error && <Alert tone="error">{error}</Alert>}

      {result && (
        <>
          <div className={styles.rowActions}>
            <Badge tone={result.error ? "rose" : "mint"}>
              {result.httpStatus != null
                ? `HTTP ${result.httpStatus}`
                : "Ingen svar"}
            </Badge>
            <span className={styles.muted}>{result.durationMs} ms</span>
            <code className={styles.guid}>{result.request}</code>
            {text && (
              <Button size="sm" variant="secondary" onClick={copy}>
                {copied ? "Kopiert" : "Kopier"}
              </Button>
            )}
          </div>
          {result.error && <Alert tone="warn">{result.error}</Alert>}
          {empty && (
            <Alert tone="info">
              Bonnetid svarte, men fant ingen moské med {result.orgNr}.
            </Alert>
          )}
          {text && <pre className={styles.rawJson}>{text}</pre>}
        </>
      )}
    </Card>
  );
};

export default BonnetidRawPanel;
